import { PrimaryButton, TextButton } from '../../../../widgets/buttons';
import { Icon, IconProps } from '../../../../widgets/icon';
import { QuestionDialog, useDialogManager } from '../../../../widgets/dialog';
import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { backofficeUrl, T } from '../../../../../shared/helpers';
import LogoZakeke from '../../../../../shared/svg/logoZakeke';
import { useProductConfigurationChangeStep } from '../../state';
import DesktopWideIcon from './icons/desktop-wide.svg';
import DesktopIcon from './icons/desktop.svg';
import MobileIcon from './icons/mobile.svg';

const CloseButtonContent = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    ${Icon} {
        width: 20px;
        height: 20px;
    }
`;

const HeaderContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    height: 56px;
    background-color: #eceff1;
    padding: 0 12px;

    & > * {
        flex: 1;
    }
`;

const LinkLogo = styled.a`
    > svg {
        height: 44px;
        width: 140px;
    }
`;

const HeaderActionsContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 24px;
    align-items: center;
    justify-content: flex-end;
    justify-items: flex-end;
`;

const HeaderActionsDivider = styled.div`
    height: 32px;
    width: 1px;
    background-color: rgba(0, 0, 0, 0.15);
`;

const HelpDeskPlaceholder = styled.div`
    width: 116px;
    height: 100%;
`;

const HeaderControlsContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 12px;
    align-items: center;
    justify-content: center;
`;

const HeaderControlIcon = styled(Icon)<IconProps & { $active?: boolean; $large?: boolean }>`
    width: 28px;
    height: 28px;
    ${(props) =>
        props.$large &&
        css`
            width: 32px;
            height: 32px;
        `}
    color: #1a1c1d;
    ${(props) =>
        props.$active &&
        css`
            color: inherit;
        `}
`;

type CloseButtonProps = {
    onClick: () => void;
};
const CloseButton: FC<CloseButtonProps> = ({ onClick }) => {
    return (
        <TextButton onClick={onClick}>
            <CloseButtonContent>{T._('Close', 'Admin')}</CloseButtonContent>
        </TextButton>
    );
};

type HeaderToolbarProps = {
    closeOverlay?: () => void;
    previewSize?: 'desktop-wide' | 'desktop' | 'mobile';
    changePreviewSize?: (size: 'desktop-wide' | 'desktop' | 'mobile') => void;
    isShoppingPreview?: boolean;
    onSave?: () => void; // Callback per il salvataggio
    isSaving?: boolean; // Stato per indicare se il salvataggio è in corso
};

const HeaderToolbar: FC<HeaderToolbarProps> = ({ previewSize, changePreviewSize, isShoppingPreview, closeOverlay, onSave, isSaving }) => {
    const { showDialog, closeDialog } = useDialogManager();
    const changeStep = useProductConfigurationChangeStep();

    return (
        <HeaderContainer>
            {
                <LinkLogo href={backofficeUrl('/Dashboard')}>
                    <LogoZakeke />
                </LinkLogo>
            }
            {isShoppingPreview && (
                <HeaderControlsContainer>
                    <HeaderControlIcon $active={previewSize === 'desktop-wide'} $hoverable onClick={() => changePreviewSize!('desktop-wide')}>
                        <DesktopWideIcon />
                    </HeaderControlIcon>
                    <HeaderControlIcon $active={previewSize === 'desktop'} $hoverable onClick={() => changePreviewSize!('desktop')}>
                        <DesktopIcon />
                    </HeaderControlIcon>
                    <HeaderControlIcon $active={previewSize === 'mobile'} $hoverable onClick={() => changePreviewSize!('mobile')}>
                        <MobileIcon />
                    </HeaderControlIcon>
                </HeaderControlsContainer>
            )}
            <HeaderActionsContainer>
                <CloseButton
                    onClick={() => {
                        console.log('close');
                        // è l'overlay del tree editor
                        closeOverlay && closeOverlay();
                        showDialog(
                            'close-dialog',
                            <QuestionDialog
                                message={
                                    <React.Fragment>
                                        <p>
                                            {T._('You will lose all unsaved changes.', 'Admin')}
                                            <br />

                                            {T._('Do you wish to continue?', 'Admin')}
                                        </p>
                                    </React.Fragment>
                                }
                                buttonYesLabel={T._('Confirm', 'Admin')}
                                buttonNoLabel={T._('Cancel', 'Admin')}
                                onYesClick={() => {
                                    closeDialog('close-dialog');
                                    changeStep('3D-product-configuration-steps');
                                    return;
                                }}
                                onNoClick={() => {
                                    closeDialog('close-dialog');
                                }}
                            />
                        );
                    }}
                />
                <HeaderActionsContainer />
                <HeaderActionsDivider />
                <TextButton
                    onClick={() => {
                        console.log('passo a scene editor');
                        changeStep('3D-product-configurator-scene-editor');
                    }}
                >
                    {T._('Scene Editor', 'Admin')}
                </TextButton>
                <TextButton
                    onClick={() => {
                        console.log('passo a composer');
                        changeStep('3D-product-configurator-composer');
                    }}
                >
                    {T._('Composer', 'Admin')}
                </TextButton>
                <TextButton
                    onClick={() => {
                        console.log('passo a shopping preview');
                        changeStep('3D-product-configurator-shopping-preview');
                    }}
                >
                    {T._('Shopping preview', 'Admin')}
                </TextButton>
                {
                    <PrimaryButton
                        disabled={isSaving}
                        onClick={() => {
                            console.log('Save button clicked');
                            if (onSave) {
                                onSave();
                            } else {
                                console.log('salvo - no onSave callback provided');
                            }
                        }}
                    >
                        {isSaving ? T._('Saving...', 'Admin') : T._('Save', 'Admin')}
                    </PrimaryButton>
                }
                <HelpDeskPlaceholder />
            </HeaderActionsContainer>
        </HeaderContainer>
    );
};

export default HeaderToolbar;
